// Local-echo persistence helpers for outgoing messages. Lifted from
// apps/web/src/features/chat/WebChatRoom.tsx (the `persistSentMessage`
// / `persistFailedMessage` pair) so apps/desktop can share the same
// path. Storage access is injected through the `MessagesPersister`
// and `ConversationsPersister` adapters; the caller is responsible
// for resolving its identity (senderAccountId) and surfacing it on
// the `MessageRecord` it passes in.
//
// Why the BE doesn't echo our own send back:
// the BE fan-out explicitly excludes the sender's device (see
// services/api/internal/sync/send.go), so without this local-echo
// the bubble the user just typed would never appear in their own
// thread.

import { log } from '../log/log';
import { lastMessagePreview } from './lastMessagePreview';
import type {
  ConversationsPersister,
  MessageRecord,
  MessagesPersister,
} from './persistenceAdapters';

export interface PersistPendingMessageOptions {
  readonly messages: MessagesPersister;
  readonly conversations: ConversationsPersister;
  /** Row to insert. MUST have `deliveryState: 'pending'`, a fresh `id`,
   *  and `contentHash` left empty/synthetic — the real content hash
   *  isn't known until the SDK encrypts. The bubble renders from this
   *  row immediately; `commitSentMessage` patches in the real fields
   *  after the SDK send returns. */
  readonly record: MessageRecord;
}

export interface CommitSentMessageOptions {
  readonly messages: MessagesPersister;
  readonly conversations: ConversationsPersister;
  /** Stable row id stamped at pending-write time. We patch the existing
   *  row in place so the bubble stays at its on-screen position — no
   *  reorder, no flash. */
  readonly id: string;
  /** Conversation id (for the lastMessageAt bump after commit). */
  readonly conversationId: string;
  /** Real content hash from `SentMessageEnvelope.content_hash`. */
  readonly contentHash: string;
  /** Real MLS epoch + server seq from the SDK. Used by the dedup
   *  guard that compares incoming Application envelopes against
   *  already-locally-echoed sends. */
  readonly epoch: number;
  readonly serverSeq: number;
  /** Final committed timestamp. Patched onto the row so the bubble's
   *  ordering reflects the actual SDK send time, not the pre-send
   *  optimistic stamp. */
  readonly sentAt: number;
  /** Short text preview to bump on the parent conversation row. Optional
   *  because attachment-only sends have nothing to preview. */
  readonly preview?: string;
  /** Caller-supplied accountId so the conversation row's
   *  `lastMessageSenderAccountId` stays consistent with the inserted
   *  message. */
  readonly senderAccountId: string;
}

export interface PersistSentMessageOptions {
  readonly messages: MessagesPersister;
  readonly conversations: ConversationsPersister;
  readonly record: MessageRecord;
  // Caller-supplied id of a previously-failed bubble that the user
  // clicked retry on. When set, the helper deletes that row before
  // writing the new (successful) one so the bubble's position in
  // the list stays stable. Idempotent — silently ignores a missing
  // row.
  readonly retryRowId?: string;
}

export interface PersistFailedMessageOptions {
  readonly messages: MessagesPersister;
  readonly record: MessageRecord;
}

export interface MarkMessageFailedOptions {
  readonly messages: MessagesPersister;
  readonly id: string;
}

/**
 * Local-echo PRE-send insert. The user's bubble appears on screen as
 * soon as Dexie fires its `liveQuery` for this conversation — typically
 * <20ms, vs. the 200-700ms SDK-worker + network round-trip the old
 * post-send-only insert had to wait for.
 *
 * The row is keyed by a stable caller-supplied `id` (UUID). After the
 * SDK send returns, the caller patches the row in place via
 * `commitSentMessage(...)` so the bubble's screen position never
 * changes — there's no insert/reorder flash. On SDK failure, the
 * caller switches the row to `deliveryState: 'failed'` via
 * `markMessageFailed(...)`.
 *
 * `contentHash` is empty at pending time (the real hash isn't known
 * until MLS encryption completes). The dedup-on-contentHash guard in
 * `commitSentMessage` is what protects against accidental double-
 * writes if the user double-taps send while the SDK call is in flight.
 */
export async function persistPendingMessage(
  opts: PersistPendingMessageOptions,
): Promise<void> {
  const { messages, conversations, record } = opts;
  try {
    await messages.put(record);
    // Bump the conversation row immediately so the chat list reflects
    // the freshly-typed message even before the SDK send returns.
    // Sender id + preview match the optimistic message bytes — when
    // `commitSentMessage` re-runs after the SDK send it overwrites
    // with the same values (idempotent), so a brief retry window
    // never shows stale subtitle text.
    const preview = lastMessagePreview(record.body);
    await conversations.update(record.conversationId, {
      lastMessageAt: record.sentAt,
      ...(preview !== undefined ? { lastMessagePreview: preview } : {}),
      lastMessageSenderAccountId: record.senderAccountId,
    });
  } catch (err) {
    log.error('chatroom.persistPending.failed', err as Error);
  }
}

/**
 * Patches the pending row written by `persistPendingMessage` with the
 * real fields the SDK assigned (content hash, epoch, server seq) and
 * flips `deliveryState` from 'pending' → 'sent'. Bubble doesn't
 * re-mount — the existing row's id stays the same, only its fields
 * change, so React's keyed reconciliation reuses the same DOM node
 * and the glyph just swaps from clock → single-check.
 */
export async function commitSentMessage(
  opts: CommitSentMessageOptions,
): Promise<void> {
  const {
    messages,
    conversations,
    id,
    conversationId,
    contentHash,
    epoch,
    serverSeq,
    sentAt,
    preview,
    senderAccountId,
  } = opts;
  try {
    // Defense in depth: if a peer broadcast somehow raced us and the
    // incoming-message handler already persisted THIS EXACT envelope, drop our
    // local-echo row to avoid a duplicate. Match on contentHash AND `sentAt`
    // (the HLC wall-clock) — `content_hash` is plaintext-only, so an EARLIER
    // message with identical text ("hi" sent twice) shares the contentHash but
    // has a DIFFERENT sentAt and must NOT delete this repeat. Only a row with
    // the same contentHash AND the same sentAt is the same logical message.
    const peerEcho = await messages.getByContentHash(contentHash);
    if (peerEcho && peerEcho.id !== id && peerEcho.sentAt === sentAt) {
      await messages.delete(id);
      return;
    }

    await messages.update(id, {
      contentHash,
      epoch,
      serverSeq,
      sentAt,
      deliveryState: 'sent',
    });
    await conversations.update(conversationId, {
      lastMessageAt: sentAt,
      ...(preview !== undefined ? { lastMessagePreview: preview } : {}),
      lastMessageSenderAccountId: senderAccountId,
    });
  } catch (err) {
    log.error('chatroom.commitSent.failed', err as Error);
  }
}

/**
 * Flip the existing pending row to `deliveryState: 'failed'` so the
 * UI can render a retry affordance on the offending bubble. Same
 * "patch in place" pattern as `commitSentMessage` — no row delete,
 * no insert, no reorder.
 */
export async function markMessageFailed(
  opts: MarkMessageFailedOptions,
): Promise<void> {
  const { messages, id } = opts;
  try {
    await messages.update(id, { deliveryState: 'failed' });
  } catch (err) {
    log.error('chatroom.markFailed.failed', err as Error);
  }
}

/**
 * Writes a freshly-sent message into the local messages store so it
 * appears in the bubble list immediately, and bumps the parent
 * conversation's `lastMessageAt` so the chat list panel re-sorts the
 * active chat to the top.
 *
 * Dedupes on contentHash so a retry that succeeds twice (unlikely
 * after the transport's own dedupe but cheap insurance) doesn't end
 * up doubling-up the row.
 *
 * @deprecated Use `persistPendingMessage` + `commitSentMessage` for
 * local-echo (instant bubble). This single-shot helper is kept for
 * call sites that don't yet need the pending-state visibility
 * (e.g. profile-intro / receipts), which the host now wires
 * through the new pair.
 */
export async function persistSentMessage(
  opts: PersistSentMessageOptions,
): Promise<void> {
  const { messages, conversations, record, retryRowId } = opts;
  try {
    // Dedupe a true duplicate only — match contentHash AND sentAt. Plaintext-
    // only content_hash means an earlier identical-text message shares the
    // hash but has a different sentAt and must not block this repeat.
    const existing = await messages.getByContentHash(record.contentHash);
    if (existing && existing.sentAt === record.sentAt) return;

    // Retry path: drop the previous "failed" row that the user
    // clicked retry on, then write a fresh row with the successful
    // envelope's content_hash. We don't reuse the old row's id
    // because the new content_hash is unique, so the contentHash
    // index would conflict on update.
    if (retryRowId !== undefined) {
      try {
        await messages.delete(retryRowId);
      } catch {
        // Ignore — the row might already be gone if the user
        // clicked retry twice. Idempotency is the goal.
      }
    }

    await messages.put(record);

    // Bump conversation lastMessageAt + push a fresh preview so the
    // list panel re-sorts the active chat to the top AND the row's
    // "John: hey, are you free?" subtitle updates instantly. Use the
    // record's `sentAt` rather than a fresh Date.now() so all writes
    // are stamped consistently. The preview formatter is shared with
    // the incoming-message path so self vs peer previews are
    // byte-identical for the same source text.
    const preview = lastMessagePreview(record.body);
    await conversations.update(record.conversationId, {
      lastMessageAt: record.sentAt,
      ...(preview !== undefined ? { lastMessagePreview: preview } : {}),
      lastMessageSenderAccountId: record.senderAccountId,
    });
  } catch (err) {
    log.error('chatroom.persistSent.failed', err as Error);
  }
}

/**
 * Writes a "failed" placeholder row when `conv.send` throws so the
 * chat surface can render a retry button on the offending bubble.
 *
 * The caller is expected to surface a deterministic id on the
 * `MessageRecord` (e.g. the original failed row's id when the user
 * clicks retry) so retries on the same bubble idempotently overwrite
 * — `put` semantics keep the bubble's position stable.
 */
export async function persistFailedMessage(
  opts: PersistFailedMessageOptions,
): Promise<void> {
  const { messages, record } = opts;
  try {
    await messages.put(record);
  } catch (err) {
    log.error('chatroom.persistFailed.failed', err as Error);
  }
}
